import { useCallback, useState } from "react";

import { Alert } from "../../../../ui/alert";
import { Checkbox } from "../../../../ui/checkbox";
import { Label } from "../../../../ui/label";
import { Spinner } from "../../../../ui/spinner";

import { getEnvironmentProps } from "../../../../../utils/environment";

import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";

import {
  apiFetchNotOkHandler,
  errorHandler
} from "../../../../../APIs/errorHandler";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";
import { Button } from "../../../../eds";
import EDSTabs from "../../../../eds/EDSTabs";
import { ProcessedScanResult, ScanConfiguration } from "../SpaceScanner.types";

/**
 * Scan Type
 */
type ScanType = {
  id: string;
  label: string;
};

/**
 * Scan Types
 */
const scanTypes: ScanType[] = [
  {
    id: "all-seated",
    label: "All seated"
  },
  {
    id: "seated-and-standing",
    label: "Seated and standing"
  }
];

/**
 * Space Scanner Stage 1 Props
 */
type SpaceScannerStage1Props = {
  onResult: (result: ProcessedScanResult[], config: ScanConfiguration) => void;
  onClose: () => void;
};

/**
 * Configure and perform scan (Stage 1)
 * @param param0
 * @returns
 */
export const SpaceScannerStage1 = ({ onResult }: SpaceScannerStage1Props) => {
  const { connectionStatus } = useDeviceContext();
  const { date, venueConfig } = useDiaryContext();
  const { isFlagEnabled } = useFeatureFlagContext();
  const [selectedScanType, setSelectedScanType] = useState<ScanType>(
    scanTypes[0]
  );
  const [isGoOverCapacitySelected, setIsGoOverCapacitySelected] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  /**
   * Perform scan
   */
  const performScan = useCallback(async () => {
    if (connectionStatus === "none") {
      setIsLoading(false);
      setError(
        "An internet connection is required to use the Space Optimiser tool. Please check your internet connection and try again."
      );

      return;
    }

    setIsLoading(true);

    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);

    const allowOverCapacity = !!isGoOverCapacitySelected;
    const dateString = date.toISOString().split("T")[0];
    const searchSeatedCapacity = selectedScanType.id === "seated-and-standing";
    const includeExplanation = isFlagEnabled("showAICoPilot");

    try {
      const result = await fetch(
        `${environmentVars?.CORE_API_BASE_URL}/venues/${venueConfig?._id}/optimise-booking-areas?allowOverCapacity=${allowOverCapacity}&date=${dateString}&searchSeatedCapacity=${searchSeatedCapacity}${includeExplanation ? "&includeExplanation=true" : ""}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!result.ok) {
        setIsLoading(false);
        setError("Something went wrong. Please try again.");
        apiFetchNotOkHandler(result, "Error performing scan");

        return;
      }

      const data = (await result.json()).payload.results;
      setIsLoading(false);

      const config: ScanConfiguration = {
        allowOverCapacity: !!isGoOverCapacitySelected,
        searchSeatedCapacity: selectedScanType.id === "seated-and-standing"
      };
      onResult(data, config);
    } catch (error) {
      setIsLoading(false);
      setError("Something went wrong. Please try again.");
      errorHandler(error, "Error performing scan");
    }
  }, [isGoOverCapacitySelected, date, selectedScanType, connectionStatus]);

  return (
    <div className="flex flex-1 flex-col h-full">
      <div className="flex items-center py-3 sm:py-[var(--panelPadding)]">
        <p className="text-sm leading-relaxed sm:text-base">
          You can use Space Optimiser to live-check the diary, to make sure you
          are utilising the space in your venue to its most efficient capacity.
          It scans your bookings and tables and provides an optimised plan of
          which tables to use for which bookings. After the scan, you can accept
          the changes and these are automatically moved in your diary and
          floorplan.
        </p>
      </div>
      <div className="flex-1 overflow-auto h-0">
        {error && <Alert variant="destructive">{error}</Alert>}
        {isLoading && (
          <div className="flex justify-center p-4">
            <Spinner className="size-8" />
          </div>
        )}
        {!isLoading && !error && (
          <div className="flex flex-row flex-wrap content-center justify-evenly items-center">
            <div className="py-2 flex flex-col flex-wrap content-center justify-center items-center">
              <EDSTabs
                tabs={scanTypes}
                selectedTab={selectedScanType.id}
                onTabChange={(tabId) => {
                  const newScanType = scanTypes.find((t) => t.id === tabId);
                  if (newScanType) {
                    setSelectedScanType(newScanType);
                  }
                }}
              />
            </div>
            <div className="py-2 flex flex-col flex-wrap content-center justify-center items-center">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="overCapacity"
                  checked={isGoOverCapacitySelected}
                  onCheckedChange={(checked) => {
                    setIsGoOverCapacitySelected(checked as boolean);
                  }}
                  className="border h-6 w-6 border-solid border-[hsla(180,70%,45%,0.2)]"
                />
                <Label htmlFor="overCapacity" className="font-bold">
                  Go over capacity if no area found?
                </Label>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-2 pt-3 sm:gap-4 sm:pt-[var(--panelPadding)]">
        <Button
          className="min-h-[48px] min-w-[80px] flex-1 text-sm sm:min-h-[44px] sm:text-base"
          disabled={isLoading || !!error}
          onClick={() => {
            performScan();
          }}
        >
          {isLoading ? "Scanning..." : "Scan areas"}
        </Button>
      </div>
    </div>
  );
};
